import { AppointmentCancellationReason, AppointmentModifiedReasonType } from '@server/prisma-generated/enums';
import { CANCELLATION_REASON_ICONS, CANCELLATION_REASON_LABELS } from '~/components/appointments/cancellationReasons';
import { Label } from '~/components/ui/label';
import { Switch } from '~/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import LocalDateTime from '~/lib/localdatetime';
import { cn } from '~/lib/utils';

export const LATE_THRESHOLD_HOURS = 48;

type HistoryEntryType = AppointmentModifiedReasonType | 'NO_SHOW';

export type AppointmentHistoryEntry = {
    id: string;
    createdAt: Date | string | null;
    type: HistoryEntryType;
    reasonCode: AppointmentCancellationReason | null;
    reason: string;
    note: string;
    hoursBefore: number | null;
    previousStartsAt: Date | string | null;
    newStartsAt: Date | string | null;
    actorName: string | null;
};

const MOVE_TYPES: HistoryEntryType[] = [
    AppointmentModifiedReasonType.CUSTOMER_MOVED,
    AppointmentModifiedReasonType.COMPANY_MOVED,
];

const CANCEL_TYPES: HistoryEntryType[] = [
    AppointmentModifiedReasonType.CUSTOMER_CANCELLED,
    AppointmentModifiedReasonType.COMPANY_CANCELLED,
];

const ACTION_LABELS: Record<HistoryEntryType, string> = {
    [AppointmentModifiedReasonType.CUSTOMER_MOVED]: 'MOVED',
    [AppointmentModifiedReasonType.COMPANY_MOVED]: 'MOVED',
    [AppointmentModifiedReasonType.CUSTOMER_CANCELLED]: 'CANCELLED',
    [AppointmentModifiedReasonType.COMPANY_CANCELLED]: 'CANCELLED',
    [AppointmentModifiedReasonType.COMPANY_DECLINED]: 'DECLINED',
    [AppointmentModifiedReasonType.COMPANY_CALLED_OFF]: 'CALLED_OFF',
    NO_SHOW: 'NO_SHOW',
};

export const formatDateTime = (date: Date | string) =>
    LocalDateTime.fromUTCDate(new Date(date), 'Europe/Berlin').format('dd.MM.yy HH:mm');

const isMove = (entry: AppointmentHistoryEntry) => MOVE_TYPES.includes(entry.type);
const isMovedByKalia = (entry: AppointmentHistoryEntry) =>
    isMove(entry) && entry.reasonCode === AppointmentCancellationReason.CANCELLED_BY_CLINIC;
const isLate = (entry: AppointmentHistoryEntry) =>
    entry.hoursBefore !== null && entry.hoursBefore < LATE_THRESHOLD_HOURS;

export const countMovedByKalia = (entries: AppointmentHistoryEntry[]) => entries.filter(isMovedByKalia).length;

export const countLateMoves = (entries: AppointmentHistoryEntry[]) =>
    entries.filter((entry) => isMove(entry) && !isMovedByKalia(entry) && isLate(entry)).length;

export const countLateCancellations = (entries: AppointmentHistoryEntry[]) =>
    entries.filter((entry) => CANCEL_TYPES.includes(entry.type) && isLate(entry)).length;

export const countCalledOff = (entries: AppointmentHistoryEntry[]) =>
    entries.filter((entry) => entry.type === AppointmentModifiedReasonType.COMPANY_CALLED_OFF).length;

/** Red: caused by the customer. Yellow: caused by Kalia. */
export type HistoryTone = 'red' | 'yellow';

const TONE_CARD_CLASSES: Record<HistoryTone, string> = {
    red: 'border-red-400 bg-red-100',
    yellow: 'border-yellow-400 bg-yellow-100',
};

const TONE_TEXT_CLASSES: Record<HistoryTone, string> = {
    red: 'text-red-800',
    yellow: 'text-yellow-800',
};

const TONE_ROW_CLASSES: Record<HistoryTone, string> = {
    red: 'bg-red-100 hover:bg-red-200',
    yellow: 'bg-yellow-100 hover:bg-yellow-200',
};

const getEntryTone = (entry: AppointmentHistoryEntry): HistoryTone | null => {
    if (entry.type === AppointmentModifiedReasonType.COMPANY_CALLED_OFF || isMovedByKalia(entry)) {
        return 'yellow';
    }
    if (entry.type === 'NO_SHOW' || ((isMove(entry) || CANCEL_TYPES.includes(entry.type)) && isLate(entry))) {
        return 'red';
    }
    return null;
};

export const isHighlightedEntry = (entry: AppointmentHistoryEntry) => getEntryTone(entry) !== null;

export function HighlightedOnlySwitch({ checked, onCheckedChange }: { checked: boolean; onCheckedChange: (checked: boolean) => void }) {
    return (
        <div className="flex items-center gap-2">
            <Switch id="history-highlighted-only" checked={checked} onCheckedChange={onCheckedChange} />
            <Label htmlFor="history-highlighted-only">Nur rote und gelbe Ereignisse anzeigen</Label>
        </div>
    );
}

export function HistoryStatCard({
    label,
    count,
    tone,
    compact = false,
}: {
    label: string;
    count: number;
    tone: HistoryTone;
    compact?: boolean;
}) {
    return (
        <div className={cn('rounded-lg border', compact ? 'px-2 py-1' : 'p-3', count > 0 && TONE_CARD_CLASSES[tone])}>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={cn('font-semibold', compact ? 'text-base' : 'text-2xl', count > 0 && TONE_TEXT_CLASSES[tone])}>{count}</p>
        </div>
    );
}

export function AppointmentHistoryTable({ entries }: { entries: AppointmentHistoryEntry[] }) {
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Zeitpunkt</TableHead>
                    <TableHead>Von</TableHead>
                    <TableHead>Aktion</TableHead>
                    <TableHead>Grund</TableHead>
                    <TableHead className="text-right">Std. vorher</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {entries.map((entry) => {
                    const isNoShow = entry.type === 'NO_SHOW';
                    const tone = getEntryTone(entry);
                    const reasonLabel = entry.reasonCode
                        ? isMovedByKalia(entry)
                            ? 'Von Kalia verschoben'
                            : CANCELLATION_REASON_LABELS[entry.reasonCode]
                        : entry.reason || '–';
                    const ReasonIcon = entry.reasonCode ? CANCELLATION_REASON_ICONS[entry.reasonCode] : null;
                    const isByCompany = !isNoShow && entry.type.startsWith('COMPANY_');

                    return (
                        <TableRow key={entry.id} className={cn(tone && TONE_ROW_CLASSES[tone])}>
                            <TableCell className="align-top">{entry.createdAt ? formatDateTime(entry.createdAt) : '–'}</TableCell>
                            <TableCell className="align-top">
                                <div>{isByCompany ? 'Kalia' : 'Kunde'}</div>
                                {entry.actorName && (
                                    <div className="text-xs text-muted-foreground">
                                        {isNoShow ? `markiert von ${entry.actorName}` : entry.actorName}
                                    </div>
                                )}
                            </TableCell>
                            <TableCell className="align-top">
                                <div className="font-medium">{ACTION_LABELS[entry.type]}</div>
                                {isMove(entry) && entry.previousStartsAt && entry.newStartsAt && (
                                    <div className="text-xs text-muted-foreground">
                                        {formatDateTime(entry.previousStartsAt)} → {formatDateTime(entry.newStartsAt)}
                                    </div>
                                )}
                            </TableCell>
                            <TableCell className="align-top whitespace-normal">
                                <div className="flex items-start gap-2">
                                    {ReasonIcon && <ReasonIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                                    <span>{reasonLabel}</span>
                                </div>
                                {entry.note && <div className="mt-1 text-xs text-muted-foreground">{entry.note}</div>}
                            </TableCell>
                            <TableCell className={cn('align-top text-right', tone && entry.hoursBefore !== null && cn('font-semibold', TONE_TEXT_CLASSES[tone]))}>
                                {entry.hoursBefore !== null ? `${entry.hoursBefore}h` : '–'}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}
